import { createFileRoute } from '@tanstack/react-router'
import {
  verifyCertificate,
  type PublicCertificateView,
  type VerificationResult,
} from '@/lib/queries/certificate-public'

/**
 * Verificação pública do certificado (W4-6).
 *
 * 🔴 Esta rota mora na RAIZ de `routes/` — fora de `_app` (que roda
 * `requireStudentAuth` no `beforeLoad`) e fora de `_auth` (que roda
 * `requireGuest` e chutaria o visitante logado para o dashboard). Precedente:
 * `routes/health.ts`.
 *
 * O motivo não é técnico, é de quem usa: quem confere um diploma é um empregador
 * ou uma banca. Não tem conta, não vai criar uma, e um formulário de login no
 * caminho transforma "conferir o diploma" em "desistir".
 *
 * ⚠️ Por isso também não há nada de sessão aqui: nem `useUser`, nem sidebar, nem
 * topbar. Qualquer componente do `_app` arrastado para cá reintroduz a
 * dependência de contexto que o layout público não fornece.
 */
export const Route = createFileRoute('/certificado/$codigo')({
  loader: ({ params }) => verifyCertificate(params.codigo),

  /**
   * O certificado carrega nome de pessoa e é um fato datado: não muda enquanto o
   * visitante olha a página, e não deve ser recarregado a cada foco de janela —
   * cada recarga come uma das 20 consultas por minuto do rate limit da rota.
   */
  staleTime: Infinity,
  shouldReload: false,

  head: () => ({
    meta: [
      { title: 'Verificação de certificado' },
      /**
       * 🔴 `noindex` não é detalhe de SEO. A página expõe nome completo de uma
       * pessoa real; indexada, o Google vira um diretório pesquisável de quem
       * concluiu quais cursos — exatamente a listagem que a API se recusa a
       * entregar (por isso o código não deriva de `uuidv7()`).
       */
      { name: 'robots', content: 'noindex, nofollow' },
    ],
  }),

  component: VerificationPage,
})

// ============================================================================
// FORMATAÇÃO
// ============================================================================

/**
 * Fuso EXPLÍCITO, não o do ambiente.
 *
 * Esta página é renderizada no servidor e hidratada no navegador. Sem `timeZone`
 * fixo, o contêiner (UTC) e o visitante (UTC-3) formatam datas diferentes para o
 * mesmo instante e o React acusa mismatch de hidratação — numa data que pode
 * virar o dia anterior no papel impresso.
 */
const DATE_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
  timeZone: 'America/Sao_Paulo',
})

/**
 * ⚠️ Aceita `Date` de propósito: o reviver do Eden Treaty converte string com
 * cara de data em `Date` do lado do cliente. Chamar método de string direto no
 * valor derruba a página (já aconteceu neste repo).
 */
function formatDate(value: string | Date | null | undefined): string | null {
  if (!value) return null
  const data = value instanceof Date ? value : new Date(value)
  return Number.isNaN(data.getTime()) ? null : DATE_FORMATTER.format(data)
}

/** `ABCDEFGHJKMNPQ` → `ABCDE FGHJK MNPQ`: 15 símbolos são para ser DIGITADOS de um papel. */
function groupCode(code: string): string {
  return code.replace(/(.{5})(?=.)/g, '$1 ')
}

// ============================================================================
// CSS DE IMPRESSÃO
// ============================================================================

/**
 * Não existe geração de PDF no projeto — imprimir pelo navegador resolve o caso
 * real (anexar o diploma a um processo seletivo). O que este bloco garante:
 *
 *  1. **Papel branco.** O app inteiro é dark (`__root.tsx` fixa `class="dark"` e
 *     injeta `html,body{background:oklch(0.145 …)}`). Impresso como está, sai um
 *     retângulo preto — ou, com "imprimir cores de fundo" ligado, gasta a
 *     metade de um cartucho. O `!important` é o que vence a regra do root.
 *  2. **A4 com margem de 14mm** ≈ 182mm ≈ ~43rem úteis. ⚠️ Por isso a folha não
 *     usa NENHUM breakpoint acima de `sm`: um `md:` (48rem) não dispara nessa
 *     largura, as grades empilham e o documento dobra de página. A grade abaixo
 *     é `auto-fit`/`minmax`, que não depende de media query nenhuma.
 *  3. **Some o que não é documento** — botão de imprimir, instruções.
 */
const PRINT_CSS = `
@media print {
  @page { size: A4 portrait; margin: 14mm; }

  html, body {
    background: #fff !important;
    color: #111 !important;
    color-scheme: light !important;
  }

  .nao-imprime { display: none !important; }

  /*
     A moldura da tela some no papel — e o background junto. Sem ele o fundo
     dark do app aparece como uma FAIXA PRETA à direita da folha em toda
     impressão com "gráficos de fundo" ligada, porque o max-w-2xl do <main> não
     cobre a largura do A4. Flagrado no PDF de prova, não na leitura do código.
     (Sem crase aqui dentro: a string é template literal e a crase a fecharia.)
  */
  .pagina-verificacao {
    padding: 0 !important;
    min-height: 0 !important;
    display: block !important;
    background: #fff !important;
  }
  .pagina-verificacao > main { max-width: none !important; }

  .folha {
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    color: #111 !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    /* O certificado é uma peça só: quebrar ao meio inutiliza a impressão. */
    break-inside: avoid;
  }

  .folha .tinta-forte { color: #111 !important; }
  .folha .tinta-suave { color: #444 !important; }
  .folha .painel { background: #fff !important; border-color: #999 !important; }
  .folha .selo { background: #fff !important; border-color: #111 !important; color: #111 !important; }
}

.codigo-verificacao { font-variant-ligatures: none; }

@media (forced-colors: active) {
  .selo { border: 1px solid CanvasText; }
}
`

const FACTS_GRID = 'grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(11rem,1fr))]'

// ============================================================================
// PÁGINA
// ============================================================================

function VerificationPage() {
  const result = Route.useLoaderData()
  const { codigo: code } = Route.useParams()

  return (
    <div className="pagina-verificacao min-h-screen bg-background px-4 py-10 flex items-start justify-center">
      <style dangerouslySetInnerHTML={{ __html: PRINT_CSS }} />

      <main className="w-full max-w-2xl space-y-6">
        <header className="nao-imprime text-center space-y-1">
          <h1 className="text-lg font-semibold text-foreground">Verificação de certificado</h1>
          <p className="text-sm text-muted-foreground">
            Confira abaixo se este certificado foi mesmo emitido e se continua válido.
          </p>
        </header>

        <VerificationContent result={result} queriedCode={code} />
      </main>
    </div>
  )
}

function VerificationContent({
  result,
  queriedCode,
}: {
  result: VerificationResult
  queriedCode: string
}) {
  switch (result.state) {
    case 'valid':
      return <CertificateSheet certificate={result.certificate} valid />

    case 'revoked':
      return (
        <CertificateSheet
          certificate={result.certificate}
          valid={false}
          revokedAt={formatDate(result.revokedAt)}
          queriedCode={queriedCode}
        />
      )

    case 'invalid_code':
      return (
        <Notice
          tone="neutral"
          title="Este código não tem o formato de um certificado"
          queriedCode={queriedCode}
        >
          Um código de certificado tem 15 caracteres, entre letras e números — sem
          as letras <strong>I</strong>, <strong>L</strong>, <strong>O</strong> e{' '}
          <strong>U</strong> e sem os dígitos <strong>0</strong> e <strong>1</strong>,
          que se confundem ao copiar de um papel. Confira o que foi digitado.
        </Notice>
      )

    case 'not_found':
      return (
        <Notice
          tone="alert"
          title="Nenhum certificado com este código"
          queriedCode={queriedCode}
        >
          O código está bem formado, mas não corresponde a nenhum certificado
          emitido por esta escola. Confira caractere por caractere; se estiver
          certo, o documento não veio daqui.
        </Notice>
      )

    /**
     * 🔴 Falha nossa NÃO é veredito sobre o diploma. Esta tela diz "não deu para
     * conferir agora" e manda tentar de novo — nunca "não encontrado", que quem
     * lê entende como "é falso" e quem apresenta o certificado paga.
     */
    case 'unavailable':
      return (
        <Notice
          tone="neutral"
          title={
            result.rateLimitExceeded
              ? 'Muitas consultas em pouco tempo'
              : 'Não foi possível verificar agora'
          }
          queriedCode={queriedCode}
        >
          {result.rateLimitExceeded ? (
            <>Aguarde um minuto e recarregue esta página.</>
          ) : (
            <>
              Isto é uma falha nossa, e <strong>não</strong> quer dizer que o
              certificado seja inválido. Recarregue a página em alguns instantes.
            </>
          )}
          {import.meta.env.DEV && (
            <span className="mt-2 block font-mono text-xs opacity-70">
              dev: {result.detail}
            </span>
          )}
        </Notice>
      )
  }
}

// ============================================================================
// A FOLHA — o que vai para o papel
// ============================================================================

function CertificateSheet({
  certificate,
  valid,
  revokedAt,
  queriedCode,
}: {
  certificate: PublicCertificateView | null
  valid: boolean
  revokedAt?: string | null
  queriedCode?: string
}) {
  /**
   * Revogado com o corpo do certificado ausente é possível no contrato da API
   * (`certificado` é opcional lá). Sem os dados não há folha para imprimir — só
   * o fato, que já é a informação que importa.
   */
  if (!certificate) {
    return (
      <Notice tone="alert" title="Este certificado foi revogado" queriedCode={queriedCode}>
        A escola cancelou este documento{revokedAt ? ` em ${revokedAt}` : ''}. Ele
        existiu, mas não vale mais.
      </Notice>
    )
  }

  const completedDate = formatDate(certificate.completedAt)
  const issuedDate = formatDate(certificate.issuedAt)

  return (
    <article className="folha rounded-2xl border border-border bg-card p-8 space-y-8">
      <Seal valid={valid} revokedAt={revokedAt ?? null} />

      <div className="space-y-2">
        <p className="tinta-suave text-sm uppercase tracking-wide text-muted-foreground">
          Certificado de conclusão
        </p>
        <h2 className="tinta-forte text-3xl font-bold leading-tight text-foreground">
          {certificate.studentName}
        </h2>
        <p className="tinta-suave text-base text-muted-foreground">
          concluiu o programa
        </p>
        <p className="tinta-forte text-xl font-semibold text-foreground">
          {certificate.programTitle}
        </p>
      </div>

      <dl className={FACTS_GRID}>
        {/*
          A data de CONCLUSÃO vem primeiro e a de emissão fica como nota de
          rodapé: é a conclusão que o documento afirma. Emissão manual de
          histórico pode ser meses depois — mostrar a emissão como se fosse a
          conclusão datava o diploma errado.
        */}
        <Fact label="Concluído em" value={completedDate ?? 'Não informado'} />
        <Fact label="Aulas do programa" value={String(certificate.totalLessons)} />
        <Fact label="Emitido em" value={issuedDate ?? '—'} />
      </dl>

      <div className="painel rounded-xl border border-border bg-secondary/40 p-4">
        <p className="tinta-suave text-xs uppercase tracking-wide text-muted-foreground">
          Código de verificação
        </p>
        <p className="codigo-verificacao tinta-forte mt-1 font-mono text-lg tracking-[0.15em] text-foreground">
          {groupCode(certificate.code)}
        </p>
      </div>

      <PrintButton />
    </article>
  )
}

function Seal({ valid, revokedAt }: { valid: boolean; revokedAt: string | null }) {
  if (valid) {
    return (
      <p className="selo inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-sm font-medium text-emerald-400">
        <span aria-hidden="true">✓</span>
        Certificado válido
      </p>
    )
  }

  return (
    <p className="selo inline-flex items-center gap-2 rounded-full border border-destructive/40 bg-destructive/10 px-3 py-1 text-sm font-medium text-destructive">
      <span aria-hidden="true">✕</span>
      Revogado{revokedAt ? ` em ${revokedAt}` : ''}
    </p>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="tinta-suave text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="tinta-forte mt-1 text-base font-medium text-foreground">{value}</dd>
    </div>
  )
}

function PrintButton() {
  return (
    <button
      type="button"
      onClick={() => window.print()}
      className="nao-imprime w-full rounded-xl border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary"
    >
      Imprimir ou salvar em PDF
    </button>
  )
}

// ============================================================================
// OS ESTADOS SEM FOLHA
// ============================================================================

function Notice({
  tone,
  title,
  queriedCode,
  children,
}: {
  tone: 'neutral' | 'alert'
  title: string
  queriedCode?: string
  children: React.ReactNode
}) {
  const borderClass = tone === 'alert' ? 'border-destructive/40' : 'border-border'

  return (
    <section className={`rounded-2xl border ${borderClass} bg-card p-8 space-y-4`}>
      <h2 className="text-xl font-semibold text-foreground">{title}</h2>
      <p className="text-sm leading-relaxed text-muted-foreground">{children}</p>

      {queriedCode && (
        <p className="text-sm text-muted-foreground">
          Código consultado:{' '}
          <span className="codigo-verificacao font-mono text-foreground break-all">
            {queriedCode}
          </span>
        </p>
      )}
    </section>
  )
}
